import { fetchHTML } from './utils-fetch-html.js';
import { runSupabaseGET, getSupabaseBaseURL } from './utils-supabase.js';
import { d as debounce } from './debounce-CFtxQv9K.js';

class PredictiveSearchResults extends HTMLElement {
  abortController;

  connectedCallback() {
    this.options = {
      minimumSearchTermLength: 1,
      searchId: this.dataset.searchId,
      hideElement: this.dataset.hideElement,
    };

    // Get the search form
    this.search = document.querySelector(`#${this.options.searchId}`);
    if (!this.search) return;

    if (this.options.hideElement) {
      this.elementsToHide = document.querySelector(`#${this.options.hideElement}`);
    }

    this.hiddenProductContainer = this.querySelector('[data-hidden-product-container]');
    // Get the predictive search controller (button in header)
    this.predictiveSearchControllerText = document.querySelector(
      '[data-predictive-search-controller-text]'
    );

    // Handle search input
    this.unsubscribeSearchInput = subscribe(
      `search:input:${this.dataset.searchId}`,
      ({ value }) => {
        this.enableLoaders();
        this.updateSearch(value);
        //set button text to the search term
        this.predictiveSearchControllerText.textContent = value;
      }
    );

    // Handle predictive search suggestion click
    const predictiveSearchSuggestions = document.querySelectorAll(
      '[data-predictive-search-suggestion]'
    );

    predictiveSearchSuggestions.forEach((suggestion) => {
      suggestion.addEventListener('click', (event) => {
        const suggestionValue = event.target.dataset.predictiveSearchSuggestion;
        // get the input element by name
        const inputElement = this.search.querySelector(`input[name="q"]`);
        // set input value to the suggestion
        inputElement.value = suggestionValue;
        document.querySelector('[data-search-clear]').ariaHidden = length === 0;

        // Update results
        this.updateSearch(suggestionValue);
        // set button text to the search term
        this.predictiveSearchControllerText.textContent = suggestionValue;
      });
    });

    // Handle search clear button
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-search-clear]')) {
        this.search.clearSearchInput();
      }
      // Handle hidden product remove button. Can't be done inside fetchSearchResults due to the replacing of markup
      if (event.target.closest('[data-hidden-product-remove]')) {
        const listItem = event.target.closest('li');
        if (listItem) {
          this.handleHiddenProductRemove(listItem);
        }
      }
    });

    // Handle search clear from search component
    this.unsubscribeSearchClear = subscribe(`search:clear:${this.dataset.searchId}`, () => {
      this.onClearSearchInput();
    });

    // When rendered in a drawer, subscribe to the drawer open-event to clear the input and results
    this.drawerId = this.closest('drawer-element')?.id;
    if (this.drawerId) {
      this.unsubscribeDrawerOpened = subscribe(`drawer:opened:${this.drawerId}`, () => {
        const inputElement = this.search.querySelector(`input[name="q"]`);
        const value = this.search.querySelector(`input[name="q"]`).value;
        const length = inputElement.value.length;
        // this.search.clearSearchInput();
        // this.clearSearchResults();
        //If input is not empty, get results for the input
        if (value) {
          this.updateSearch(value);
          document.querySelector('[data-search-clear]').ariaHidden = length === 0;
          inputElement.focus();
          inputElement.setSelectionRange(length, length);
        } else {
          inputElement.focus();
        }
      });
    }

    this.unsubscribeMachineChange = subscribe('machine:change', () => {
      this.syncViewAllResultsUrl();
    });

    this.syncViewAllResultsUrl();
  }

  disconnectedCallback() {
    this.unsubscribeSearchInput();
    this.unsubscribeSearchClear();
    if (this.drawerId) {
      this.unsubscribeDrawerOpened();
    }
    this.unsubscribeMachineChange?.();
  }

  /**
   * Clear the search results
   */
  clearSearchResults() {
    this.innerHTML = '';
  }

  /**
   * Clear the search input
   */
  onClearSearchInput() {
    this.ariaHidden = true;
    this.hideOnSearchResults(false);
  }

  /**
   * Enable loaders
   */
  enableLoaders() {
    this.querySelectorAll('[data-loader]').forEach((loader) => {
      loader.ariaHidden = false;
    });
  }

  /**
   * Disable loaders
   */
  disableLoaders() {
    setTimeout(() => {
      this.querySelectorAll('[data-loader]').forEach((loader) => {
        loader.ariaHidden = true;
      });
    }, 50);
  }

  /**
   * Update the search
   */
  updateSearch = debounce((searchTerm) => this.getSearchResults(searchTerm), 300);

  /**
   * Get the search results
   */
  getSearchResults(searchTerm) {
    searchTerm = searchTerm.trim();
    if (searchTerm.length < this.options.minimumSearchTermLength) {
      this.clearSearchResults();
      this.hideOnSearchResults(false);
      this.ariaHidden = true;
      return;
    }
    this.fetchSearchResults(searchTerm);
  }

  /**
   * Hide other elements when the predictive search results are shown
   */
  hideOnSearchResults(hide = true) {
    if (this.elementsToHide) this.elementsToHide.ariaHidden = hide;
  }

  /**
   * Fetch the search results
   */
  async fetchSearchResults(searchTerm) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    try {
      const searchUrl = routes.predictive_search_url;
      const htmlPromise = fetchHTML(
        `${searchUrl}?q=${searchTerm}&section_id=fetch-predictive-search`,
        { signal: this.abortController.signal }
      );

      // Supabase call
      const queryURL = this.createHiddenProductsFetchURL(searchTerm);
      const hiddenProductsPromise = runSupabaseGET(queryURL);

      const [html, hiddenProductsResults] = await Promise.all([htmlPromise, hiddenProductsPromise]);
      const resultsMarkup = html.querySelector('predictive-search-results');

      // If we have a match on one of the hidden products, don't show the
      // no-results markup. Show the hidden-product-form instead, with the
      // necessary markup to send a shopify contact form submission.
      if (hiddenProductsResults.length > 0) {
        const noResultsContainer = resultsMarkup.querySelector('[data-search-no-results');
        if (noResultsContainer) noResultsContainer.remove();

        const hiddenProductsForm = resultsMarkup.querySelector('[data-hidden-product-container]');
        if (hiddenProductsForm) {
          hiddenProductsForm.classList.remove('hidden');
          const hiddenProductsList = hiddenProductsForm.querySelector(
            '[data-hidden-products-list]'
          );
          const template = hiddenProductsForm.querySelector('template[data-input-template]');
          if (hiddenProductsList && template) {
            hiddenProductsResults.forEach((productResult, index) => {
              const clone = template.content.cloneNode(true);
              this.populateHiddenProductTemplate(
                productResult.sku,
                productResult.title,
                clone,
                index
              );
              hiddenProductsList.appendChild(clone);
            });
          }
        }
      }

      this.innerHTML = '';
      this.innerHTML = resultsMarkup.innerHTML;
      this.syncViewAllResultsUrl();
      this.ariaHidden = false;
      this.hideOnSearchResults();
    } catch (error) {
      console.error('Error fetching search results:', error);
    } finally {
      this.disableLoaders();
    }
  }

  createHiddenProductsFetchURL(searchTerm) {
    const supabaseBaseURL = getSupabaseBaseURL();
    //For performance reasons, we ony include title in the query if the
    //searchterm is at least 3 characters, otherwise most rows of the hidden
    //products will match.

    const filterOr = `sku.eq.${searchTerm}`;
    return `${supabaseBaseURL}&select=sku,title&or=(${filterOr})`;
  }

  populateHiddenProductTemplate(sku, title, markup, index) {
    const inputSKU = markup.querySelector('[data-sku]');
    const inputTitle = markup.querySelector('[data-title]');
    const visualSKU = markup.querySelector('[data-visual-sku]');
    const visualTitle = markup.querySelector('[data-visual-title]');
    if (!inputSKU || !inputTitle || !visualSKU || !visualTitle) return;

    inputSKU.value = sku;
    inputSKU.name = inputSKU.name.replace('sku-hidden-product-x', `sku-hidden-product-${index}`);
    inputTitle.value = title;
    inputTitle.name = inputTitle.name.replace(
      'title-hidden-product-x',
      `title-hidden-product-${index}`
    );

    visualSKU.innerText = `(${sku})`;
    visualTitle.innerText = title;
  }

  populateAddHiddenProductToFormTemplate(sku, title) {
    const template = this.querySelector('template[data-add-to-form-template]');
    if (!template) return;
    const clone = template.content.cloneNode(true);
    const SKULabel = clone.querySelector('[data-sku]');
    const titleLabel = clone.querySelector('[data-title]');

    if (!SKULabel || !titleLabel) return;
    SKULabel.innerText = `(${sku})`;
    SKULabel.dataset.sku = sku;
    titleLabel.innerText = title;
    titleLabel.dataset.title = title;

    const button = clone.querySelector('[data-add-to-form-button]');
    if (!button) return;
    button.addEventListener('click', this.handleAddHiddenProductToFormButtonClick.bind(this));
    return clone;
  }

  handleHiddenProductRemove(listItem) {
    const sku = listItem.querySelector('[data-sku]').value;
    const title = listItem.querySelector('[data-title]').value;

    // Make the template and add it for adding it back to form
    const addToFormTemplate = this.populateAddHiddenProductToFormTemplate(sku, title);
    const addToFormContainer = this.querySelector('[data-hidden-product-add-to-form-container]');
    if (addToFormContainer) {
      addToFormContainer.appendChild(addToFormTemplate);
    }

    // If this is the only listItem and its being removed, we disabled the form.
    if (listItem.parentNode.children.length === 1) {
      const formSubmitButton = this.querySelector('[data-form-submit-button]');
      formSubmitButton.disabled = true;
      listItem.remove();
    } else if (listItem.parentNode.children.length > 1) {
      const parent = listItem.parentNode;
      listItem.remove();
      // Change the name of the inputs so they correctly reflect the number in
      // the 'selected products' list.
      Array.from(parent.children).forEach((child, index) => {
        const titleInput = child.querySelector('[data-title]');
        const skuInput = child.querySelector('[data-sku]');
        if (titleInput && skuInput) {
          titleInput.name = `contact[title-hidden-product-${index + 1}]`;
          skuInput.name = `contact[sku-hidden-product-${index + 1}]`;
        }
      });
    }

    // Set the heading for non-selected options visible again
    addToFormContainer.dataset.empty = 'false';
  }

  handleAddHiddenProductToFormButtonClick(e) {
    const sku = e.currentTarget.querySelector('[data-sku]').dataset.sku;
    const title = e.currentTarget.querySelector('[data-title]').dataset.title;

    const template = this.querySelector('template[data-input-template]');
    const clone = template.content.cloneNode(true);

    const hiddenProductsList = this.querySelector('[data-hidden-products-list]');
    const index = hiddenProductsList.children.length + 1;

    this.populateHiddenProductTemplate(sku, title, clone, index);
    hiddenProductsList.appendChild(clone);

    const formSubmitButton = this.querySelector('[data-form-submit-button]');
    formSubmitButton.disabled = false;

    if (e.currentTarget.parentNode.children.length === 1) {
      e.currentTarget.parentNode.dataset.empty = 'true';
    }
    e.currentTarget.remove();
  }

  /**
   * Ensure the "View all results" button carries the active machine filter.
   */
  syncViewAllResultsUrl() {
    const viewAllLink = this.querySelector('[data-view-all-results]');
    if (!viewAllLink) return;

    if (!viewAllLink.dataset.baseHref) {
      viewAllLink.dataset.baseHref = viewAllLink.getAttribute('href');
    }

    const machineController = window.getMachineController?.();
    const machineData = machineController?.getCurrentState?.();
    const machineParam = 'filter.p.m.custom.models';
    const baseHref = viewAllLink.dataset.baseHref;
    const url = new URL(baseHref, window.location.origin);

    if (machineData?.modelGid) {
      url.searchParams.set(machineParam, machineData.modelGid);
    } else {
      url.searchParams.delete(machineParam);
    }

    const relativeUrl = `${url.pathname}${url.search}${url.hash}`;
    viewAllLink.setAttribute('href', relativeUrl);
  }
}

customElements.define('predictive-search-results', PredictiveSearchResults);
